'use strict';
import {
    FrontSide,
    LinearFilter,
    LinearMipmapLinearFilter,
    ShaderMaterial,
    NearestFilter,
    Color,
    Matrix3, CanvasTexture, DoubleSide, Quaternion, Sphere,Box3, Matrix4, Vector3, LinearSRGBColorSpace,MeshBasicMaterial
} from 'three';
import {
    Buildings,
    isDesktop, isIOS,
    isMobile,
    isTablet,
    projectMode, resourcesFolder,
    settingsData,
    threeJsVariables,
} from '../../settings';
import { DebugGoogleTilesRenderer, DebugTilesRenderer, GoogleTilesRenderer, TilesRenderer } from '3d-tiles-renderer';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import {getUrlParam, isCylinderDisplayModeEqual} from '../../functions';
import { Group } from 'three';
import { ViewMode} from "../../common";
import { EnvironmentTypes } from '../../common';
import { TimesOfDay } from '../liveAnimations/litemode/dayNight/TimesOfDay';
import { RepeatWrapping } from 'three';
import { MeshStandardMaterial } from 'three';
import { OrbitDayAndNight } from '../liveAnimations/DayNight/OrbitDayAndNight';
import { ColorCorrection } from './colorCorrection';
import { LUT } from '../post-processing/Passes/LUT';
import { OptimizationTask } from '../gui/FPS';
import { Hmap } from './Hmap';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module';
import { MathUtils } from 'three';
import { fixModelUV } from './autoProjectScript';
import { Cloudflare } from '../api/Cloudflare';
import { BmbyMode } from '../mode/BmbyMode';

const envGroup = new Group();
let tilesEnvMapIntensity = 1;
let tilesColor = '#ffffff';
let tilesEmissive = '#000000';
let defaultTileMaterial;
let shadowParams = {};

export function setDefaultTileMaterial (material) {
    defaultTileMaterial = material;
}

export function setTilesShadowParams (params) {
    shadowParams = params;
}


export function setTilesEnvMapIntensity (intensity) {
    tilesEnvMapIntensity = intensity;
}

export function setTilesColor (color) {
    tilesColor = color;

    if (threeJsVariables.tilesRenderer){
        threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
            scene.traverse(tile =>{
                if (tile.isMesh) {
                    if (tile.material.isShaderMaterial) {
                        tile.material.uniforms.color.value.setStyle(color,LinearSRGBColorSpace);
                    } else {
                        tile.material.color.setStyle(color,LinearSRGBColorSpace);
                    }
                }
            })
        })
    }
}

export function setTilesEmissive (color) {
    tilesEmissive = color;
    if (threeJsVariables.tilesRenderer){
        threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
            scene.traverse(tile =>{
                if ( tile.material && !tile.material.isShaderMaterial) {
                    tile.material.emissive.setStyle(color,LinearSRGBColorSpace);
                }
            })
        })
    }
}

export const basic_tile = new ShaderMaterial({
    uniforms: {
        map         : { value: null },
        hmap        : { value: null },
        uvTransform : { value: new Matrix3() },
        fogColor    : { value: new Color()  },
        fogDensity  : { value: 0.0  },
        color       : { value: new Color(1,1,1) },
        gamma       : { value: 1.0 },
        uOpacity    : { value: 1 }
    },
    vertexShader: [
        'uniform sampler2D hmap;',
        'uniform mat3 uvTransform;',
        'varying vec2 lUv;',
        'varying float z;',
        'void main() {',
            'lUv = (uvTransform*vec3( uv, 1 )).xy;',
            'vec4 rp = modelMatrix * vec4( position, 1.0 );',
            'float size = 512.0;',
            'float scale = 1.0/1024.0;',
            'float h = texture2D(hmap, vec2((rp.x+size)*scale,(rp.z+size)*scale)).r;',
            'if (h!=0.0){',
                'rp.y = (h-0.5)*128.0;',
            '}',
            'gl_Position = projectionMatrix * viewMatrix * rp;',
            'z = -gl_Position.z;',
        '}'].join(''),
    fragmentShader: [
        'uniform vec3 fogColor;',
        'uniform float fogDensity;',
        'uniform sampler2D map;',
        'varying vec2 lUv;',
        'varying float z;',
        'uniform float gamma;',
        'uniform float uOpacity;',
        'uniform vec3 color;',
        'void main(){',
            'vec4 c = texture2D(map, lUv);',
            'c = vec4(c.rgb * color * gamma,uOpacity);',
            'float fogDepth = z;',
            'float fogFactor = 1.0 - exp( -fogDensity * fogDensity * fogDepth * fogDepth );',
            'gl_FragColor = vec4(mix( c.rgb, fogColor, fogFactor ), uOpacity);',
        '}'].join(''),
    side       : FrontSide, //DoubleSide,
    depthTest  : true,
    depthWrite : true,
    transparent: false,
});

function correctTilesPosition (tiles,env) {
    const {position, rotation, scale} = env;

    tiles.group.position.set(position.x,position.y,position.z);
    tiles.group.rotation.set(rotation.x,rotation.y,rotation.z);
    tiles.group.scale.set(scale.x,scale.y,scale.z);

}

export function environmentToggle () {

    if (projectMode.LiteMode.isActive() || BmbyMode.active) return;

    let {active,environmentTypes} = config.resources.environments;
    if (!environmentTypes || !Boolean(environmentTypes.length))
        environmentTypes = threeJsVariables.bundleConfig.tiles.settings.environmentTypes;

    if (!active){
        return;
    }

    let env;

    if (isDesktop){
        env = environmentTypes.filter(type => type.apply.filter(envType => envType.device === 'desktop')[0])[0];
    } else if (isMobile){
        env = environmentTypes.filter(type => type.apply.filter(envType => envType.device === 'mobile')[0])[0];
    } else {
        env = environmentTypes.filter(type => type.apply.filter(envType => envType.device === 'web')[0])[0];
    }

    switch (env.type) {
        case EnvironmentTypes.CesiumEnv:
                loadCesiumEnvironment(env);
            break;
        // case EnvironmentTypes.CesiumMultiEnv:
                // loadMultiEnvironment();
            // break;
        case EnvironmentTypes.TilesEnv:
        case EnvironmentTypes.GooglePhotorealistic:
               loadTilesEnvironment(env);
            break;
    }
}

function setAdditionalSettings (tile) {

    if (isMobile){
        const mobileSettings = tile.userData.settings.mobile;
        if (mobileSettings){
            Object.entries(mobileSettings).forEach(([key,value]) =>{
               tile[key] = value;
            });
        }
    } else {
        const desktopSettings = tile.userData.settings.desktop;
        if (desktopSettings){
            Object.entries(desktopSettings).forEach(([key,value]) =>{
                tile[key] = value;
            });
        }
    }
}

function setupAndAddToSceneAllEnvironmentParts (response) {
    const envGroup = new Group();
    envGroup.name = 'async env';
    response.forEach(env => {
        setAdditionalSettings(env);
        envGroup.add(env.group);
    });

    threeJsVariables.scene.add(envGroup);
    threeJsVariables.renderList.reorder();

    console.log(envGroup);
    console.log(response);

    function update(){
        requestAnimationFrame(update);
        if (settingsData.activeViewMode === ViewMode.Orbit){
            response.forEach(env =>{
               env.update();
            });
        }
    }

    update();
}

function loadEnv (env) {
    return new Promise(resolve => {
       const { cesium } = config.cesiumEnvironment;
       let url = new URL( `https://api.cesium.com/v1/assets/${env.id}/endpoint`);
       url.searchParams.append('access_token', cesium.cesiumAccessToken);

       let tiles;

        fetch(url, {mode: 'cors'})
          .then((res) => {

              if (res.ok) {
                  return res.json();
              } else {
                  return Promise.reject(`${res.status} : ${res.statusText}`);
              }

          })
          .then((json) => {
              url = new URL(json.url);
              const version = url.searchParams.get('v');

              tiles = new TilesRenderer(url);
              tiles.userData = {
                  settings: env,
                  isActive: true,
                  tilesOnScene: false,
              };
              tiles.setCamera(threeJsVariables.perspectiveCamera);
              tiles.setResolutionFromRenderer(threeJsVariables.perspectiveCamera, threeJsVariables.renderer);
              tiles.fetchOptions.headers = {};
              tiles.fetchOptions.headers.Authorization = `Bearer ${json.accessToken}`;

              tiles.preprocessURL = (uri) => {
                  const exp = parseJwt(json.accessToken).exp;
                  const now = new Date().getTime() / 1000;

                  if (exp < now){
                      const tokenRequest = getNewToken();
                      tokenRequest.then((newToken) =>{
                          json.accessToken = newToken;
                          uri = new URL( uri );
                          if ( /^http/.test( uri.protocol ) ) {

                              uri.searchParams.append( 'v', version );

                          }
                          return uri.toString();
                      });
                  } else {
                      uri = new URL( uri );
                      if ( /^http/.test( uri.protocol ) ) {

                          uri.searchParams.append( 'v', version );

                      }
                      return uri.toString();
                  }
              };

              tiles.onLoadTileSet = (e) => {
                  const box = new Box3();
                  const matrix = new Matrix4();
                  tiles.getOrientedBounds(box, matrix);
                  const position = new Vector3().setFromMatrixPosition(matrix);
                  const distanceToEllipsoidCenter = position.length();

                  const surfaceDirection = position.normalize();
                  const up = new Vector3(0, 1, 0);
                  const rotationToNorthPole = rotationBetweenDirections(surfaceDirection, up);

                  // tiles.group.quaternion.x = rotationToNorthPole.x;
                  // tiles.group.quaternion.y = rotationToNorthPole.y;
                  // tiles.group.quaternion.z = rotationToNorthPole.z;
                  // tiles.group.quaternion.w = rotationToNorthPole.w;
                  // tiles.group.position.y = -distanceToEllipsoidCenter;
                  correctTilesPosition(tiles,env);
              };

              tiles.onLoadModel = function (scene) {
                  scene.traverse(objMesh => {
                      if (objMesh.material && objMesh.material.map ) {
                          const image = new CanvasTexture(objMesh.material.map.image);
                          image.wrapS = RepeatWrapping;
                          image.wrapT = RepeatWrapping;
                          image.offset.set(0,0);
                          image.repeat.set(1,1);
                          image.flipY = false;
                          objMesh.material = new MeshBasicMaterial({
                              map: image,
                          });
                          // objMesh.material.map.encoding = THREE.LinearEncoding;
                      }
                  });
              };

              setup();
          })
          .catch(err => {
              console.error('Unable to get ion tileset:', err);
          });


        function setup() {
            tiles.fetchOptions.mode = 'cors';

            const dracoLoader = new DRACOLoader();
            dracoLoader.setDecoderPath('js/libs/gltf/');

            const loader = new GLTFLoader(tiles.manager);
            loader.setDRACOLoader(dracoLoader);

            tiles.manager.addHandler(/\.gltf$/, loader);

            resolve(tiles);
        }
    });
}

export function loadCesiumEnvironment(env) {
    const {cesiumSettings,scale, position,rotation} = env.settings;

    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        environmentShow();
        return;
    }

    let url;
    let tiles;
    const offsetParent = new Group();

    const cesiumIdManual = getUrlParam('cesiumid');
    const tokenManual = getUrlParam('token');
    let urlManual = `https://api.cesium.com/v1/assets/${cesiumIdManual}/endpoint`;
    const cesiumUrl = cesiumSettings.cesiumURL.replace('{{cesiumAssetsID}}', String(cesiumSettings.cesiumAssetsID));

    url = new URL( cesiumIdManual && tokenManual ? urlManual : cesiumUrl);
    url.searchParams.append('access_token', tokenManual || cesiumSettings.cesiumAccessToken);

    fetch(url, {mode: 'cors'})
      .then((res) => {

          if (res.ok) {
              return res.json();
          } else {
              return Promise.reject(`${res.status} : ${res.statusText}`);
          }

      })
      .then((json) => {
          url = new URL(json.url);
          const version = url.searchParams.get('v');

          settingsData.needsRerender = true;

          tiles = new TilesRenderer(url);

          // window.tiles = tiles;
          tiles.displayBoxBounds = true;
          tiles.userData = {
              isActive: true,
              tilesOnScene: false,
          };
          threeJsVariables.tilesRenderer = tiles;
          tiles.setCamera(threeJsVariables.perspectiveCamera);
          tiles.setResolutionFromRenderer(threeJsVariables.perspectiveCamera, threeJsVariables.renderer);
          tiles.fetchOptions.headers = {};
          tiles.fetchOptions.headers.Authorization = `Bearer ${json.accessToken}`;

          setEnvironmentSettings(env,tiles);

          // if (isMobile){
          //     tiles.errorTarget = 18;
          //     tiles.errorThreshold = 1000;
          // } else {
          //     tiles.errorTarget = 5;
          //     tiles.errorThreshold = 1000;
          // }

          tiles.manager.onProgress = function (url, itemsLoaded, itemsTotal) {
             // if (!tiles.userData.isActive){
             //     tiles.lruCache.itemList = [];
             //     tiles.lruCache.callbacks.clear();
             //     tiles.downloadQueue.items=[];
             //     tiles.downloadQueue.callbacks.clear();
             // }
          };

          tiles.manager.onLoad = () =>{
              // if (tiles.downloadQueue.callbacks.size === 0){
              //     tiles.userData.tilesOnScene = true;
              // }
          };

          tiles.preprocessURL = (uri) => {
              const exp = parseJwt(json.accessToken).exp;
              const now = new Date().getTime() / 1000;

              if (exp < now){
                  const tokenRequest = getNewToken(env);
                  tokenRequest.then((newToken) =>{
                      json.accessToken = newToken;
                      uri = new URL( uri );
                      if ( /^http/.test( uri.protocol ) ) {

                          uri.searchParams.append( 'v', version );

                      }
                      return uri.toString();
                  });
              } else {
                  uri = new URL( uri );
                  if ( /^http/.test( uri.protocol ) ) {

                      uri.searchParams.append( 'v', version );

                  }
                  return uri.toString();
              }
          };

          tiles.onLoadTileSet = (e) => {
              const box = new Box3();
              const matrix = new Matrix4();
              tiles.getOrientedBounds(box, matrix);
              const position = new Vector3().setFromMatrixPosition(matrix);
              const distanceToEllipsoidCenter = position.length();

              const surfaceDirection = position.normalize();
              const up = new Vector3(0, 1, 0);
              const rotationToNorthPole = rotationBetweenDirections(surfaceDirection, up);

              tiles.group.quaternion.x = rotationToNorthPole.x;
              tiles.group.quaternion.y = rotationToNorthPole.y;
              tiles.group.quaternion.z = rotationToNorthPole.z;
              tiles.group.quaternion.w = rotationToNorthPole.w;
              tiles.group.position.y = -distanceToEllipsoidCenter;
          };

          tiles.onLoadModel = function (scene) {
              // tiles.userData.isActive && tiles.update();
              // console.log('update tiles render');
              scene.traverse(objMesh => {
                  if (objMesh.material && objMesh.material.map ) {
                      // const image = new THREE.CanvasTexture(objMesh.material.map.image);
                      // image.wrapS = THREE.RepeatWrapping;
                      // image.wrapT = THREE.RepeatWrapping;
                      // image.offset.set(0,0);
                      // image.repeat.set(1,1);
                      // image.flipY = false;
                      objMesh.material = new MeshBasicMaterial({
                          map: objMesh.material.map,
                          side: DoubleSide,
                      });
                      objMesh.material.map.colorSpace = LinearSRGBColorSpace;
                  }
              });
          };

          setupTiles();
      })
      .catch(err => {
          console.error('Unable to get ion tileset:', err);
      });


    function setupTiles() {
        tiles.fetchOptions.mode = 'cors';

        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('js/libs/gltf/');

        const loader = new GLTFLoader(tiles.manager);
        loader.setDRACOLoader(dracoLoader);

        tiles.manager.addHandler(/\.gltf$/, loader);
        offsetParent.add(tiles.group);
    }

    if (!cesiumIdManual && !tokenManual){
        offsetParent.scale.set(scale.x, scale.y, scale.z);
        offsetParent.position.set(position.x, position.y, position.z);
        offsetParent.rotation.set(rotation.x, rotation.y, rotation.z);
    }

    offsetParent.name = 'async env';
    offsetParent.updateMatrixWorld(true);

    threeJsVariables.scene.add(offsetParent);

    // renderLoop();

    // function renderLoop() {
    //     requestAnimationFrame(renderLoop);
    //     if (!tiles) return;

         // tiles.userData.isActive && tiles.update();
        // tiles.update();



        // if (tiles.group.children.length === 0){
        //     // console.log('render');
        //     tiles.userData.isActive && tiles.update();
        // }
        //
        // if (settingsData.drag_move || settingsData.wheelIsUse){
        //    tiles.userData.isActive && tiles.update();
        // }
    // }

}

function setEnvironmentSettings (env,tilesRender) {
    let settings;

    if (isDesktop){
        settings = env.apply.filter(envType => envType.device === 'desktop')[0];
    } else if (isMobile || isTablet){
        settings = env.apply.filter(envType => envType.device === 'mobile')[0];
    }else{
        settings = env.apply.filter(envType => envType.device === 'web')[0];
    }

    const config = settings.config;

    const params = {
        minSize: getUrlParam('min') ? Number(getUrlParam('min')) : config.minSize ? config.minSize : isMobile ? 100 : 600,
        maxSize: getUrlParam('max') ? Number(getUrlParam('max')) : config.maxSize ? config.maxSize : isMobile ? 200 : 800,
        errorTarget: getUrlParam('target') ? Number(getUrlParam('target')) : config.errorTarget,
        maxDepth: config.maxDepth ? config.maxDepth : Infinity,
        errorThreshold: config.errorThreshold ? config.errorThreshold : Infinity
    };

    if (getUrlParam('depth')) {
        let value = Number(getUrlParam('depth'));
        if (value === 0) {
            value = Infinity;
        }
        params.maxDepth = value;
    }

    if (getUrlParam('threshold')) {
        let value = Number(getUrlParam('threshold'));
        if (value === 0) {
            value = Infinity;
        }
        params.errorThreshold = value;
    }
    
    params.errorTarget = threeJsVariables.quality.tilesErrorTarget(params.errorTarget);
    params.maxDepth = threeJsVariables.quality.tilesMaxDepth(params.maxDepth);


    if (devModeEnvironmentParams.isGuiOnScene){
        tilesRender.errorTarget = devModeEnvironmentParams.errorTarget;
        tilesRender.errorThreshold = devModeEnvironmentParams.errorThreshold;
        tilesRender.maxDepth = devModeEnvironmentParams.maxDepth;
    } else {
        Object.entries(params).forEach(([key,value]) =>{
            if (key === 'minSize' || key === 'maxSize') {
                tilesRender.lruCache[key] = value;
            } else {
                tilesRender[key] = value;
            }
        });
    }
}

function getNewToken (env) {
    const {cesiumAccessToken,cesiumAssetsID,cesiumURL} = env.settings.cesiumSettings;

    const cesiumUrl = cesiumURL.replace('{{cesiumAssetsID}}', String(cesiumAssetsID));

    let url = new URL( cesiumUrl);
    url.searchParams.append('access_token', cesiumAccessToken);

    return new Promise(async (resolve, reject) => {
       const response = await fetch(url, {mode: 'cors'});
       if (response.ok){
           resolve(response.json().accessToken);
       } else {
           reject(`${response.status} : ${response.statusText}`);
       }
    });
}

function parseJwt (token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
};


function rotationBetweenDirections(dir1, dir2) {

    const rotation = new Quaternion();
    const a = new Vector3().crossVectors(dir1, dir2);
    rotation.x = a.x;
    rotation.y = a.y;
    rotation.z = a.z;
    rotation.w = 1 + dir1.clone().dot(dir2);
    rotation.normalize();

    return rotation;
}

const devModeEnvironmentParams = {
    displayBoxBounds: false,
    errorTarget: 5,
    errorThreshold: 12,
    maxDepth: 20,
    isGuiOnScene: false,
    'changeDisplayBoxBounds': () => changeDisplayBoxBounds()
}

function changeDisplayBoxBounds() {
    const {scene} = threeJsVariables;
    devModeEnvironmentParams.displayBoxBounds = !devModeEnvironmentParams.displayBoxBounds;

    const asyncEnv = threeJsVariables.scene.getObjectByName('async env');
    asyncEnv && scene.remove(asyncEnv);

    environmentToggle();
}
function changeErrorTarget(value) {

}
function changeerrorThreshold(value) {

}

function addGuiHelper() {
    if (devModeEnvironmentParams.isGuiOnScene) return;

    const {gui} = threeJsVariables;

    const environmentFolder = gui.addFolder('environment');

    environmentFolder.add( devModeEnvironmentParams, 'errorTarget', 0, 500 ).step( 0.01 ).onChange( function ( value ) {
        threeJsVariables.tilesRenderer.errorTarget = value;
    });

    environmentFolder.add( devModeEnvironmentParams, 'errorThreshold', 0, 1000 ).step( 0.01 ).onChange( function ( value ) {
        threeJsVariables.tilesRenderer.errorThreshold = value;
    });

    environmentFolder.add( devModeEnvironmentParams, 'maxDepth', 0, 50 ).step( 1 ).onChange( function ( value ) {
        threeJsVariables.tilesRenderer.maxDepth = value;
    });

    environmentFolder.add(devModeEnvironmentParams, 'changeDisplayBoxBounds');

    devModeEnvironmentParams.isGuiOnScene = true;
}

export const setHMap = (t)=>{
    t.minFilter = NearestFilter
    t.magFilter = NearestFilter
    t.generateMipmaps = false
    t.flipY = false

    basic_tile.uniforms.hmap.value = t

    let tilesRender =  threeJsVariables.tilesRenderer
    if (tilesRender){
        tilesRender.forEachLoadedModel((scene,tile)=>{
            scene.traverse(o =>{
                if ( o.material) {
                    o.material.uniforms.hmap.value = t
                }
            })
        })
    }else{
        let a = threeJsVariables.scene.getObjectByName('async env')
        a?.traverse(o=>{
            if ( o.material) {
                o.material.uniforms.hmap.value = t
            }
        })
    }
}

export const setFog = (color,density)=>{

    basic_tile.uniforms.fogColor.value.set(color)
    basic_tile.uniforms.fogDensity.value = density

    let tilesRender =  threeJsVariables.tilesRenderer

    if (tilesRender){
        tilesRender.forEachLoadedModel((scene,tile)=>{
            scene.traverse(o =>{
                if ( o.material) {
                    o.material.uniforms.fogColor.value.set(color)
                    o.material.uniforms.fogDensity.value = density
                }
            })
        })
    }else if (!OrbitDayAndNight.isActive){

        let a = threeJsVariables.scene.getObjectByName('async env')
        a?.traverse(o=>{
            if ( o.material) {
                o.material.uniforms.fogColor.value.set(color)
                o.material.uniforms.fogDensity.value = density
            }
        });
    }
}

export const setColorGamma = (color,gamma)=>{
    basic_tile.uniforms.color.value.set(color)
    basic_tile.uniforms.gamma.value = gamma

    let tilesRender =  threeJsVariables.tilesRenderer

    if (tilesRender){
        tilesRender.forEachLoadedModel((scene,tile)=>{
            scene.traverse(o =>{
                if ( o.material) {
                    o.material.uniforms.color.value.set(color)
                    o.material.uniforms.gamma.value = gamma
                }
            })
        })
    }else{
        let a = threeJsVariables.scene.getObjectByName('async env')
        a?.traverse(o=>{
            if ( o.material) {
                o.material.uniforms.color.value.set(color)
                o.material.uniforms.gamma.value = gamma
            }
        })
    }
}

export function addTilesToScene() {
    // if (!isMobile) threeJsVariables.scene.add(envGroup);

    if (projectMode.LiteMode.isActive()) {

        // projectMode.LiteMode.addToScene();

       return new Promise(resolve => {
           // setTimeout(() => {
           //
           //
           //     resolve();
           // },2000);
           const interval = setInterval(() => {
               const menu = document.querySelector('.aside__container');
               const nightTexture = config.features.liteMode.images.night || false;

               if (nightTexture) {
                   const timesOfDayIsReady = TimesOfDay.isReady();

                   if (menu && timesOfDayIsReady) {
                       clearInterval(interval);
                       resolve();
                   }

               } else {

                   if (menu) {
                       clearInterval(interval);
                       resolve();
                   }

               }
           },500);
           // const liteModeInterval = setInterval(() => {
           //     const checkLayer = threeJsVariables.scene.getObjectByName('Lite mode');
           //     if (checkLayer){
           //         clearInterval(liteModeInterval);

               // }
           // },100);
       });
    }

    return new Promise(resolve => {
        const int = setInterval(() =>{

            // threeJsVariables.renderer.render(threeJsVariables.scene, threeJsVariables.perspectiveCamera);

            if (threeJsVariables.tilesRenderer) {

                settingsData.needsRerender = true;

                if (settingsData.activeViewMode === ViewMode.Cylinder || threeJsVariables.tilesRenderer.visibleTiles.size) {
                    clearInterval(int);
                    resolve();
                }

            } else {

                const asyncGroup = threeJsVariables.scene.getObjectByName('async env');

                if (config.resources.bundle.minitiles && asyncGroup && asyncGroup.children.length === config.resources.bundle.tilesCount){
                    clearInterval(int);
                    resolve();
                } else if (!config.resources.bundle.minitiles && asyncGroup) {
                    clearInterval(int);
                    resolve();
                }
            }
        },200)
    });
}

export function loadTilesEnvironment(env) {
    if (BmbyMode.active)
        return;
    const active = config.resources.environments.active;
    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (!active) {
        if (!isEnv) {
            const empty = new Group();
            empty.name = 'async env';
            threeJsVariables.scene.add(empty);
            threeJsVariables.renderList.reorder();
        }
        return;
    }


    if (isEnv) {
        // environmentShow();
        environmentShowV2();
        return;
    }

    const {position,rotation,scale,tilesSettings, offset} = env.settings;
    const {cdnRepository,projectFolder} = config;
    const offsetVector = new Vector3();

    if (offset) {
        offsetVector.set(offset.x,offset.y,offset.z);
    }

    let tilesUrl;
    let tilesRenderer;

    if (env.type === 'tiles') {
        tilesUrl = `${cdnRepository}/${projectFolder}/${resourcesFolder}/tiles/${tilesSettings.path}`;

        if (getUrlParam('dev')){
            tilesRenderer = new DebugTilesRenderer(tilesUrl);
            tilesRenderer.displayBoxBounds = devModeEnvironmentParams.displayBoxBounds;
        } else {
            tilesRenderer = new TilesRenderer(tilesUrl);
        }
    } else {
        tilesUrl = config.googleAPI.split('&')[0];

        if (getUrlParam('dev')){
            tilesRenderer = new DebugGoogleTilesRenderer(tilesUrl);
            tilesRenderer.displayBoxBounds = devModeEnvironmentParams.displayBoxBounds;
        } else {
            tilesRenderer = new GoogleTilesRenderer(tilesUrl);
        }
        tilesRenderer.setLatLonToYUp( tilesSettings.coords.lat * MathUtils.DEG2RAD, tilesSettings.coords.lng * MathUtils.DEG2RAD )
    }


    const colors = new ColorCorrection().getColors('tiles');
    const lut = LUT.getScope('tiles');
    threeJsVariables.orbitBuildingsNavigation.applyLutNightLightsSettings(lut,'tiles');
    setTilesColor(colors.color);
    setTilesEmissive(colors.emissive);

    let envMap = null;
    if (!defaultTileMaterial) {
        const buildingInstance = Buildings.getBuildingsArray()[0].instances[0];
        if (Array.isArray(buildingInstance.material)) {
            envMap = buildingInstance.material[0].envMap;
        } else {
            envMap = buildingInstance.material.envMap;
        }
    }

    settingsData.needsRerender = true;
    tilesRenderer.optimizeRaycast = false;
    threeJsVariables.tilesRenderer = tilesRenderer;
    settingsData.totalTilesCount = 0;

    const surfaceGroup = new Group();
    surfaceGroup.name = 'surfaceGroup';
    surfaceGroup.position.add(offsetVector);

    const envGroup = new Group();
    envGroup.name = 'async env';
    envGroup.add(surfaceGroup);

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('js/libs/gltf/');

    const ktx2Loader = new KTX2Loader()
        .setTranscoderPath( 'js/libs/basis/' )
        .detectSupport(threeJsVariables.renderer);

    const loader = new GLTFLoader()
        .setDRACOLoader(dracoLoader)
        .setKTX2Loader(ktx2Loader)
        .setMeshoptDecoder( MeshoptDecoder );

    tilesRenderer.manager.addHandler(/\.gltf$/, {

        parse(...args) {
            return loader.parse(...args);
        }
    });
    
    tilesRenderer.preprocessURL = uri => {
        uri = new URL( uri );
        if (env.type === 'tiles')
            uri.searchParams.append( 'v', Cloudflare.version );
        return uri.toString();
    };

    tilesRenderer.setCamera(threeJsVariables.perspectiveCamera);
    tilesRenderer.setResolutionFromRenderer(threeJsVariables.perspectiveCamera, threeJsVariables.renderer);

    setEnvironmentSettings(env,tilesRenderer);

    if (getUrlParam('dev')){

        addGuiHelper();
    }

    // tilesRenderer.manager.onProgress = (tiles)=>{
    //     // console.log(tiles);
    // };

    // tilesRenderer.preprocessURL = (uri) => {
    //     // const domain = 'https://cloud.skylineglobe.com/sg/b3dm/NeveTzedek3.420160/1416/';
    //     // uri = new URL(domain + uri);
    //     // return uri;
    // };

    //tilesRenderer.manager.onLoad = (tiles) =>{
        // if (tilesRenderer.frameCount === tilesRenderer.root.children.length){
        //     removePreloader();
        //     flyCameraAnimation();
        // }
    //};

    if (env.type === 'tiles') {
        tilesRenderer.addEventListener('load-tile-set',({tileSet}) => {
            const tilesType = Object.keys(tileSet.root.boundingVolume)[0];
            let position;
            let surfaceDirection;
            let up;
            let rotationToNorthPole;
    
            switch (tilesType) {
                case 'box':
                    if (config.resources.environments.needsBoxTransform) {
                        const box = new Box3();
                        const matrix = new Matrix4();
                        tilesRenderer.getOrientedBoundingBox(box,matrix);
                        position = new Vector3().setFromMatrixPosition(matrix);
                        const distanceToEllipsoidCenter = position.length();
                        surfaceDirection = position.normalize();
                        up = new Vector3(0,1,0);
                        rotationToNorthPole = rotationBetweenDirections(surfaceDirection,up);
                
                        surfaceGroup.quaternion.x = rotationToNorthPole.x;
                        surfaceGroup.quaternion.y = rotationToNorthPole.y;
                        surfaceGroup.quaternion.z = rotationToNorthPole.z;
                        surfaceGroup.quaternion.w = rotationToNorthPole.w;
                        surfaceGroup.position.y = - distanceToEllipsoidCenter + offsetVector.y;
                    }
            
                    break;
                case 'sphere':
                    const sphere = new Sphere();
                    tilesRenderer.getBoundingSphere(sphere);
                    const dir = new Vector3().subVectors(new Vector3(),sphere.center);
                    tilesRenderer.group.position.set(dir.x,dir.y,dir.z);
            
                    position = new Vector3().copy(tilesRenderer.group.position);
            
                    surfaceDirection = position.normalize();
                    up = new Vector3(0, -1, 0);
                    rotationToNorthPole = rotationBetweenDirections(surfaceDirection, up);
            
                    surfaceGroup.quaternion.x = rotationToNorthPole.x;
                    surfaceGroup.quaternion.y = rotationToNorthPole.y;
                    surfaceGroup.quaternion.z = rotationToNorthPole.z;
                    surfaceGroup.quaternion.w = rotationToNorthPole.w;
            
                    break;
            }
    
            settingsData.needsRerender = true;
        });
    }

    const envToggle = OrbitDayAndNight.hasBackgroundToggle;
    
    tilesRenderer.addEventListener('load-model',({scene}) => {
        settingsData.needsRerender = true;
        scene.traverse(objMesh =>{
            if ( objMesh.material) {
            
                let map = objMesh.material.map;
                if (map) {
                    map.colorSpace = LinearSRGBColorSpace;
                    map.wrapS = RepeatWrapping;
                    map.wrapT = RepeatWrapping;
                    map.offset.set(0,0);
                    map.repeat.set(1,1);
                    map.anisotropy = 2;
                    map.generateMipmaps = !map.isCompressedTexture;
                    map.magFilter = LinearFilter;
                    map.minFilter = LinearMipmapLinearFilter;
                }
            
                if (defaultTileMaterial) {
                    objMesh.material = defaultTileMaterial.clone();
                    objMesh.material.map = map;
                
                    if (objMesh.material.lightMap || objMesh.material.aoMap) {
                        fixModelUV(objMesh,objMesh.material);
                    }
                
                    if (lut) {
                        lut.include(objMesh.material);
                    } else {
                        Hmap.upgradeMaterial(objMesh.material);
                    }
                
                    if (objMesh.material.type === 'MeshBasicMaterial') {
                        objMesh.material.color.r = tilesEnvMapIntensity;
                        objMesh.material.color.g = tilesEnvMapIntensity;
                        objMesh.material.color.b = tilesEnvMapIntensity;
                    } else if (OrbitDayAndNight.isActiveAndSelected) {
                        objMesh.material.envMapIntensity = tilesEnvMapIntensity;
                    }
                    objMesh.castShadow = shadowParams.castShadow;
                    objMesh.receiveShadow = shadowParams.receiveShadow;
                    objMesh.geometry.computeVertexNormals();
                    objMesh.geometry.computeBoundsTree();
                
                } else {
                    if (envToggle) {
                    
                        objMesh.material = new MeshStandardMaterial({
                            map: map,
                            envMap: envMap,
                            envMapIntensity: tilesEnvMapIntensity,
                            color: new Color().setStyle(tilesColor,LinearSRGBColorSpace),
                            emissive: new Color().setStyle(tilesEmissive,LinearSRGBColorSpace)
                        });
                    
                        if (lut) {
                            lut.include(objMesh.material);
                        } else {
                            Hmap.upgradeMaterial(objMesh.material);
                        }
                    
                        objMesh.castShadow = shadowParams.castShadow;
                        objMesh.receiveShadow = shadowParams.receiveShadow;
                    
                        objMesh.geometry.computeVertexNormals();
                    
                    } else {
                        objMesh.material = basic_tile.clone();
                        objMesh.material.uniforms.map.value = map;
                        objMesh.material.uniforms.hmap.value = basic_tile.uniforms.hmap.value;
                        objMesh.castShadow = false;
                    }
                }
                // threeJsVariables.slicer.add(objMesh);
                settingsData.totalTilesCount++;
            }
        });
    });

    tilesRenderer.addEventListener('dispose-model',({scene}) => {
        scene.traverse( el => {
            if ( el.isMesh ) {
                lut && lut.removeShader(el.material.uuid);
                el.geometry.disposeBoundsTree();
                el.geometry.dispose();
                el.material.dispose();
            }
        });
    });

    surfaceGroup.add(tilesRenderer.group);

    envGroup.scale.set(scale.x,scale.y,scale.z);
    envGroup.position.set(position.x,position.y,position.z);
    envGroup.rotation.set(rotation.x,rotation.y,rotation.z);

    threeJsVariables.scene.add( envGroup );
    threeJsVariables.renderList.reorder();
    threeJsVariables.environment = envGroup;
    threeJsVariables.tilesRenderer = tilesRenderer;

    const tilesTask = new OptimizationTask((fps) => {
        if (fps < settingsData.fps) {
            tilesRenderer.errorTarget += 0.5;
        } else {
            tilesTask.finished = true;
        }
    },10,10);


    const updateTilesRerender = setInterval(() => {
        settingsData.needsRerender = true;
        if (tilesRenderer.visibleTiles.size > 10) {
            clearInterval(updateTilesRerender);
        }
    },100);

}

export function renderLoop() {
    if (threeJsVariables.tilesRenderer){
        threeJsVariables.tilesRenderer.update();
    }
    settingsData.needsRerender = false;
}

export function environmentHide (hideWithoutAnimation = false) {
    if (config.features.privateHouseMode || isCylinderDisplayModeEqual('show all')) return;
    const asyncEnv = threeJsVariables.scene.getObjectByName('async env');
    if (asyncEnv) {
        if (hideWithoutAnimation) {
            asyncEnv.visible = false;
        }
        // if (threeJsVariables.slicer.active) {
        //     asyncEnv.visible = false;
        //     return;
        // }
        if (!asyncEnv.visible){
            return;
        }

        asyncEnv.visible = false;

        // const mixer = GSAPAnimationMixer.getMixer('cylinderEnter');

        // if (threeJsVariables.tilesRenderer) {
        //     threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
        //         scene.traverse(child =>{
        //             if (child.isMesh){
        //                 let animation;
        //
        //                 if (child.material.isShaderMaterial) {
        //                     animation = gsap.to(child.material.uniforms.uOpacity,{
        //                         duration: GSAPAnimationMixer.duration,
        //                         ease: GSAPAnimationMixer.ease,
        //                         // transparent: true,
        //                         value: 0,
        //                         onStart: () => {
        //                             child.material.transparent = true;
        //                             child.material.needsUpdate = true;
        //                         },
        //                         // onUpdate: () => {
        //                         //     child.material.needsUpdate = true;
        //                         // },
        //                         onComplete:()=>{
        //                             asyncEnv.visible = false;
        //                         }
        //                     });
        //                 } else {
        //                     animation = gsap.to(child.material,{
        //                         duration: GSAPAnimationMixer.duration,
        //                         ease: GSAPAnimationMixer.ease,
        //                         // transparent: true,
        //                         opacity: 0,
        //                         onStart: () => {
        //                             child.material.transparent = true;
        //                             child.material.needsUpdate = true;
        //                         },
        //                         // onUpdate: () => {
        //                         //     child.material.needsUpdate = true;
        //                         // },
        //                         onComplete:()=>{
        //                             asyncEnv.visible = false;
        //                         }
        //                     });
        //                 }
        //
        //                 mixer.addAnimation(animation);
        //             }
        //         });
        //     });
        // } else {
        //     asyncEnv.traverse(element => {
        //         if (element.isMesh) {
        //             const animation = gsap.to(element.material, {
        //                 duration: GSAPAnimationMixer.duration,
        //                 ease: GSAPAnimationMixer.ease,
        //                 // transparent: true,
        //                 opacity: 0,
        //                 onStart:()=>{
        //                     element.material.transparent = true;
        //                     element.material.needsUpdate = true;
        //                 },
        //                 onComplete: () => {
        //                     asyncEnv.visible = false;
        //                 }
        //             });
        //
        //            mixer.addAnimation(animation);
        //         }
        //     });
        // }
    }
}

export function environmentShow () {

    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        isEnv.visible = true;

        // threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
        //     scene.traverse(child =>{
        //         if (child.isMesh){
        //             const envPromise = new Promise(resolve => {
        //                 gsap.to(child.material,{
        //                     duration: 1,
        //                     opacity: 1,
        //                     onStart:()=>{
        //                         scene.visible = true;
        //                     },
        //                     onComplete:()=>{
        //                         child.material.transparent = false;
        //                         resolve(true);
        //                     }
        //                 });
        //             });
        //
        //             // settingsData.animationsList.push(envPromise);
        //         }
        //     });
        // });

    }
}

export function environmentShowV2 () {
    // const animations = [];

    const isEnv = threeJsVariables.scene.getObjectByName('async env');
    if (isEnv) {
        isEnv.visible = true;
        // if (showWithoutAnimation) return;
        //
        // const mixer = GSAPAnimationMixer.getMixer('exitToOrbit');
        //
        // if (threeJsVariables.tilesRenderer){
        //     threeJsVariables.tilesRenderer.forEachLoadedModel((scene,tile)=>{
        //         scene.traverse(child =>{
        //             if (child.isMesh){
        //                 // const envPromise = new Promise(resolve => {
        //                     const defaultSettings = child.material.userData.defaultSettings;
        //                     let animation;
        //
        //                     if (child.material.isShaderMaterial) {
        //                         const animation = gsap.to(child.material.uniforms.uOpacity,{
        //                             duration: GSAPAnimationMixer.duration,
        //                             ease: GSAPAnimationMixer.ease,
        //                             // transparent: true,
        //                             value: defaultSettings ? defaultSettings.opacity : 1,
        //                             onStart:()=>{
        //                                 child.material.transparent = true;
        //                                 scene.visible = true;
        //                             },
        //                             onComplete:()=>{
        //                                 if (defaultSettings) {
        //                                     child.material.transparent = defaultSettings.transparent;
        //                                 } else {
        //                                     child.material.transparent = false;
        //                                 }
        //                             }
        //                         });
        //
        //                     } else {
        //                         animation = gsap.to(child.material,{
        //                             duration: GSAPAnimationMixer.duration,
        //                             ease: GSAPAnimationMixer.ease,
        //                             // transparent: true,
        //                             opacity: defaultSettings ? defaultSettings.opacity : 1,
        //                             onStart:()=>{
        //                                 child.material.transparent = true;
        //                                 scene.visible = true;
        //                             },
        //                             onComplete:()=>{
        //                                 if (defaultSettings) {
        //                                     child.material.transparent = defaultSettings.transparent;
        //                                 } else {
        //                                     child.material.transparent = false;
        //                                 }
        //                             }
        //                         });
        //                     }
        //                     mixer.addAnimation(animation);
        //                 // });
        //
        //                 // animations.push(envPromise);
        //             }
        //         });
        //     });
        // }else{
        //     isEnv.traverse(child =>{
        //         if (child.isMesh){
        //             // const envPromise = new Promise(resolve => {
        //                const animation = gsap.to(child.material,{
        //                     duration: GSAPAnimationMixer.duration,
        //                     ease: GSAPAnimationMixer.ease,
        //                     transparent: true,
        //                     opacity: 1,
        //                     onStart:()=>{
        //                         child.visible = true
        //                     },
        //                     onComplete:()=>{
        //                         child.material.transparent = false
        //                     }
        //                 });
        //             mixer.addAnimation(animation);
        //             // })
        //
        //             // animations.push(envPromise)
        //         }
        //     });
        // }

    }

    // return Promise.all(animations);
}


export function disposeTiles () {
    if (threeJsVariables.tilesRenderer && (isMobile || isTablet) && isIOS) {
        threeJsVariables.tilesRenderer.dispose();
    }
}
